import React, { createContext, useState, useEffect, useContext } from 'react';

// Export the context explicitly
export const CartContext = createContext();

export const CartProvider = ({ children }) => {
  const [cartItems, setCartItems] = useState([]);
  const [isCartOpen, setIsCartOpen] = useState(false);
  const [applicationReference, setApplicationReference] = useState('');
  const [userInfo, setUserInfo] = useState({
    name: '',
    email: ''
  });
  
  // Load cart and user info from localStorage on initial render
  useEffect(() => {
    const savedCartItems = localStorage.getItem('cartItems');
    if (savedCartItems) {
      setCartItems(JSON.parse(savedCartItems));
    }
    
    // Load saved application reference
    const savedReference = sessionStorage.getItem('financierReferenceCode');
    if (savedReference) {
      setApplicationReference(savedReference);
    }
    
    // Load saved user info
    const savedUserInfo = localStorage.getItem('financierUserInfo');
    if (savedUserInfo) {
      setUserInfo(JSON.parse(savedUserInfo));
    }
  }, []);

  // Save cart to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('cartItems', JSON.stringify(cartItems));
  }, [cartItems]);

  // Add item to cart
  const addToCart = (item) => {
    // Create a new unique ID for each badge, even if they're the same type
    const newItem = {
      ...item,
      quantity: 1, // Ensure each item starts with quantity of 1
      uniqueId: `${item.id || item.name}-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
    };
    
    // If the item has a reference code, store it in session storage for future use
    if (item.referenceCode) {
      sessionStorage.setItem('financierReferenceCode', item.referenceCode);
      setApplicationReference(item.referenceCode);
    }
    
    setCartItems(prevItems => [...prevItems, newItem]);
    setIsCartOpen(true);
  };

  // Remove item from cart
  const removeFromCart = (item) => {
    setCartItems(prevItems => 
      prevItems.filter(i => i.uniqueId !== item.uniqueId)
    );
  };

  // Update item quantity
  const updateQuantity = (item, change) => {
    setCartItems(prevItems => 
      prevItems.map(i => {
        if (i.uniqueId === item.uniqueId) {
          const newQuantity = (i.quantity || 1) + change;
          // Only update if the new quantity is greater than 0
          return newQuantity > 0 ? { ...i, quantity: newQuantity } : i;
        }
        return i;
      })
    );
  };

  // Calculate total - now includes quantity multiplication
  const cartTotal = cartItems.reduce(
    (total, item) => total + (item.price * (item.quantity || 1)), 
    0
  );

  const value = {
    cartItems,
    isCartOpen,
    cartTotal,
    applicationReference,
    userInfo,
    setApplicationReference,
    setUserInfo,
    setIsCartOpen,
    addToCart,
    removeFromCart,
    updateQuantity
  };

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};

export default CartContext; 